import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';

// project import
import Search from './Search';
import Message from './Message';
import Profile from './Profile';
import Localization from './Localization';
import Notification from './Notification';
import FullScreen from './FullScreen';
import Customization from './Customization';
import MobileSection from './MobileSection';
import InternetStatus from './InternetStatus';

import useConfig from 'hooks/useConfig';
import { MenuOrientation } from 'config';
import DrawerHeader from 'layout/Dashboard/Drawer/DrawerHeader';
import { isProduction } from 'utils/helper';
import { Autocomplete, Button, Divider, Popover, Stack, TextField, Typography } from '@mui/material';
import { fetchStores } from 'utils/apiHelper';
import { useApp, useAppDispatch } from 'contexts/AppContext';
import useAuth from 'hooks/useAuth';

// assets
import ZoomInOutlined from '@ant-design/icons/ZoomInOutlined';

// ==============================|| HEADER - CONTENT ||============================== //

export default function HeaderContent() {
  const { menuOrientation } = useConfig();
  const { isAdmin, roles } = useAuth();
  const canSelectStore = isAdmin || roles.includes('lab_billing') || roles.includes('pharmacy_biller');
  const { pathname } = useLocation();
  const isStoreContext =
    pathname.startsWith('/inventory/') ||
    pathname.startsWith('/invoice/') ||
    pathname === '/reception/patient-list';
  const showStoreSelector = canSelectStore && isStoreContext;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const localization = useMemo(() => <Localization />, []);

  const appState = useApp();
  const appDispatch = useAppDispatch();
  const { selectedStore } = appState;
  
  const [storeList, setStoreList] = useState([]);
  const [zoomHelpAnchor, setZoomHelpAnchor] = useState(null);

  useEffect(() => {
    document.documentElement.style.removeProperty('zoom');
    window.localStorage.removeItem('appZoom');
  }, []);

  useEffect(() => {
    if (!showStoreSelector) return;

    fetchStores("limit=1000").then((response) => {
      setStoreList(response?.results || []);
    });
  }, [showStoreSelector]);
  return (
    <>
      {menuOrientation === MenuOrientation.HORIZONTAL && !downLG && <DrawerHeader open={true} />}
      {!isProduction && (
        <>
          {!downLG && <Search />}
        </>
      )}
      {(downLG || isProduction) && <Box sx={{ width: '100%', ml: 1 }} />}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, minWidth: 0 }}>
        {!downLG && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: showStoreSelector ? 0.75 : 0 }}>
            <Button
              color="secondary"
              size="small"
              startIcon={<ZoomInOutlined />}
              aria-label="How to adjust page zoom"
              aria-controls={zoomHelpAnchor ? 'browser-zoom-help' : undefined}
              aria-haspopup="true"
              onClick={(event) => setZoomHelpAnchor(event.currentTarget)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Page zoom
            </Button>
            <Popover
              id="browser-zoom-help"
              open={Boolean(zoomHelpAnchor)}
              anchorEl={zoomHelpAnchor}
              onClose={() => setZoomHelpAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { width: 340, maxWidth: 'calc(100vw - 24px)', mt: 1 } } }}
            >
              <Stack spacing={1.25} sx={{ p: 2 }}>
                <Typography variant="h5">Adjust page size</Typography>
                <Typography variant="body2" color="text.secondary">
                  How to use your browser&apos;s built-in zoom.
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5, typography: 'body2', '& li + li': { mt: 0.75 } }}>
                  <li>Open the browser menu in the top-right corner (⋮ or ☰).</li>
                  <li>Find <strong>Zoom</strong>.</li>
                  <li>Select <strong>+</strong> to enlarge or <strong>−</strong> to reduce the page.</li>
                  <li>Select <strong>100%</strong> to return to the normal size.</li>
                </Box>
                <Divider />
                <Typography variant="caption" color="text.secondary">
                  Shortcut: ⌘ + / ⌘ − on Mac, or Ctrl + / Ctrl − on Windows.
                </Typography>
              </Stack>
            </Popover>
          </Box>
        )}
        {showStoreSelector && (
          <Autocomplete
            size="small"
            sx={{ width: { xs: 150, sm: 240, lg: 300 }, minWidth: 0 }}
            value={selectedStore || null}
            options={storeList || []}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(_, newValue) => {
              appDispatch({ type: 'SELECT_STORE', payload: newValue });
            }}
            getOptionLabel={(option) => option?.name || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Store"
                InputProps={{
                  ...params.InputProps,
                  type: "search"
                }}
              />
            )}
          />
        )}
        {!downLG && <InternetStatus />}
      </Box>

      {!isProduction && !downLG && (
        <>
          {localization}
          <Notification />
          <Message />
          <FullScreen />
          <Customization />
        </>
      )}
      {!downLG && <Profile />}
      {downLG && <MobileSection />}
    </>
  );
}